export type FetchFailureKind = "timeout" | "network" | "aborted" | "unknown";

export interface ApiFetchOptions extends RequestInit {
	/** Tempo máximo de espera pela resposta, em ms. Padrão: 30000. */
	timeoutMs?: number;
	/** Tentativas extras em caso de falha de rede (TypeError). Padrão: 2. */
	retries?: number;
	/** Base do backoff exponencial entre tentativas, em ms. Padrão: 500. */
	retryDelayMs?: number;
}

const DEFAULT_TIMEOUT_MS = 30000;
const DEFAULT_RETRIES = 2;
const DEFAULT_RETRY_DELAY_MS = 500;

export class HttpTimeoutError extends Error {
	readonly url: string;
	readonly timeoutMs: number;
	readonly cause?: unknown;

	constructor(url: string, timeoutMs: number, cause?: unknown) {
		super(`Tempo limite (${timeoutMs}ms) excedido ao chamar ${url}`);
		this.name = "HttpTimeoutError";
		this.url = url;
		this.timeoutMs = timeoutMs;
		this.cause = cause;
	}
}

export class HttpNetworkError extends Error {
	readonly url: string;
	readonly attempts: number;
	readonly cause?: unknown;

	constructor(url: string, cause: unknown, attempts: number) {
		super(`Falha de rede ao chamar ${url} após ${attempts} tentativa(s)`);
		this.name = "HttpNetworkError";
		this.url = url;
		this.attempts = attempts;
		this.cause = cause;
	}
}

export class HttpAbortedError extends Error {
	readonly url: string;
	readonly cause?: unknown;

	constructor(url: string, cause?: unknown) {
		super(`Requisição cancelada: ${url}`);
		this.name = "HttpAbortedError";
		this.url = url;
		this.cause = cause;
	}
}

function isAbortError(error: unknown): boolean {
	return error instanceof Error && error.name === "AbortError";
}

function isNetworkTypeError(error: unknown): boolean {
	return error instanceof TypeError;
}

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Substituto resiliente para `fetch`: aplica timeout via AbortController,
 * retenta falhas transitórias de rede (TypeError) com backoff exponencial e
 * propaga erros classificáveis (timeout / rede / cancelamento) em vez do
 * genérico `TypeError: Failed to fetch`. Não interfere em respostas HTTP de
 * erro (4xx/5xx) — essas continuam chegando como `Response` normal.
 */
export async function apiFetch(
	url: string,
	options: ApiFetchOptions = {}
): Promise<Response> {
	const {
		timeoutMs = DEFAULT_TIMEOUT_MS,
		retries = DEFAULT_RETRIES,
		retryDelayMs = DEFAULT_RETRY_DELAY_MS,
		signal: callerSignal,
		...fetchOptions
	} = options;

	let attempt = 0;

	for (;;) {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
		const onCallerAbort = () => controller.abort();

		if (callerSignal) {
			if (callerSignal.aborted) controller.abort();
			else callerSignal.addEventListener("abort", onCallerAbort);
		}

		try {
			return await fetch(url, {
				...fetchOptions,
				signal: controller.signal,
			});
		} catch (error) {
			if (callerSignal?.aborted) {
				throw new HttpAbortedError(url, error);
			}

			if (isAbortError(error)) {
				// timeout interno: falha rápido, sem retry (retry é só p/ rede)
				throw new HttpTimeoutError(url, timeoutMs, error);
			}

			if (isNetworkTypeError(error)) {
				if (attempt < retries) {
					attempt++;
					await delay(retryDelayMs * 2 ** (attempt - 1));
					continue;
				}
				throw new HttpNetworkError(url, error, attempt + 1);
			}

			throw error;
		} finally {
			clearTimeout(timeoutId);
			if (callerSignal) {
				callerSignal.removeEventListener("abort", onCallerAbort);
			}
		}
	}
}

/**
 * `apiFetch` para operações de escrita: nunca retenta. O backend ainda não é
 * idempotente — um `Failed to fetch` pode acontecer depois que o servidor
 * já processou o pedido, e um retry gravaria o registro duas vezes.
 * `retries: 0` vem depois do spread para que o chamador não o reative.
 * Quando o backend aceitar chave de idempotência, basta remover o override.
 */
export function apiFetchWrite(
	url: string,
	options: ApiFetchOptions = {}
): Promise<Response> {
	return apiFetch(url, { ...options, retries: 0 });
}

/** Classifica um erro de `apiFetch` para tagging/telemetria (ex.: Sentry). */
export function classifyFetchError(error: unknown): FetchFailureKind {
	if (error instanceof HttpTimeoutError) return "timeout";
	if (error instanceof HttpNetworkError) return "network";
	if (error instanceof HttpAbortedError) return "aborted";
	return "unknown";
}
